import { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { DataTable } from "../components/DataTable";
import { Modal, Field, inputCls, selectCls, btnPrimary, btnSecondary, btnDanger } from "../components/Modal";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";

const PERM_LABELS = {
  "dashboard.view": "Voir le tableau de bord", "pos.use": "Utiliser la caisse (POS)",
  "sales.view": "Voir les ventes", "sales.create": "Créer des ventes", "sales.cancel": "Annuler des ventes",
  "sales.edit_price": "Modifier les prix", "sales.discount": "Accorder des remises",
  "products.view": "Voir les produits", "products.create": "Créer des produits", "products.edit": "Modifier des produits",
  "products.delete": "Supprimer des produits", "products.see_purchase_price": "Voir prix d'achat",
  "stock.view": "Voir le stock", "stock.adjust": "Ajuster le stock", "stock.transfer": "Transferts entre dépôts",
  "inventory.manage": "Gérer les inventaires", "warehouses.manage": "Gérer les dépôts",
  "purchases.view": "Voir les achats", "purchases.create": "Créer des achats", "purchases.receive": "Réceptionner",
  "customers.view": "Voir les clients", "customers.create": "Créer des clients", "customers.edit": "Modifier clients", "customers.delete": "Supprimer clients",
  "suppliers.view": "Voir fournisseurs", "suppliers.create": "Créer fournisseurs", "suppliers.edit": "Modifier fournisseurs", "suppliers.delete": "Supprimer fournisseurs",
  "documents.view": "Voir devis/factures", "documents.create": "Créer devis/factures", "documents.delete": "Supprimer documents",
  "expenses.view": "Voir dépenses", "expenses.create": "Créer dépenses", "expenses.delete": "Supprimer dépenses",
  "cash.view": "Voir la trésorerie", "cash.manage": "Gérer la trésorerie",
  "credits.view": "Voir les créances", "debts.view": "Voir les dettes",
  "reports.view": "Voir les rapports", "reports.export": "Exporter", "reports.financial": "Voir bénéfices & marges",
  "users.manage": "Gérer les utilisateurs", "audit.view": "Voir le journal d'audit", "settings.manage": "Gérer les paramètres",
};

export default function Users() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ permissions: [], roles: {} });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "cashier", permissions: null });
  const [customPerms, setCustomPerms] = useState(false);

  const load = () => api.get("/users").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/meta/permissions").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", password: "", role: "cashier", permissions: null }); setCustomPerms(false); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role, permissions: u.permissions }); setCustomPerms(u.permissions != null); setOpen(true); };

  const togglePerm = (p) => {
    const cur = form.permissions != null ? form.permissions : [];
    setForm({ ...form, permissions: cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p] });
  };

  const save = async () => {
    try {
      const payload = { ...form, permissions: customPerms ? form.permissions : null };
      if (editing) {
        const patch = { name: payload.name, role: payload.role, permissions: payload.permissions };
        if (payload.password) patch.password = payload.password;
        await api.patch(`/users/${editing.id}`, patch);
      } else {
        await api.post("/users", payload);
      }
      toast.success(editing ? "Utilisateur modifié" : "Utilisateur créé");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async () => {
    try {
      await api.delete(`/users/${confirmDel.id}`);
      toast.success("Utilisateur supprimé");
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const columns = [
    { key: "name", label: "Nom", render: (u) => (
      <div><div className="font-semibold">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div></div>) },
    { key: "role", label: "Rôle", render: (u) => (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300">
        <ShieldCheck className="h-3 w-3" />{meta.roles[u.role] || u.role}</span>) },
    { key: "active", label: "Statut", render: (u) => u.active
      ? <span className="text-xs font-semibold text-emerald-600">Actif</span>
      : <span className="text-xs font-semibold text-rose-600">Désactivé</span> },
    { key: "created_at", label: "Créé le", render: (u) => fmtDateTime(u.created_at) },
    { key: "actions", label: "", className: "text-right", render: (u) => (
      <div className="flex justify-end gap-1">
        <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-muted"><Pencil className="h-4 w-4" /></button>
        <button data-testid={`delete-user-${u.id}`} onClick={() => setConfirmDel(u)} className="h-8 w-8 inline-flex items-center justify-center rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950"><Trash2 className="h-4 w-4" /></button>
      </div>) },
  ];

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Utilisateurs & Rôles</h1>
        <button data-testid="add-user-btn" onClick={openNew} className={btnPrimary}><Plus className="h-4 w-4" /> Nouvel utilisateur</button>
      </div>
      <DataTable testid="users-table" columns={columns} rows={rows} searchKeys={["name", "email", "role"]} />

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? `Modifier — ${editing.name}` : "Nouvel utilisateur"} wide>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Nom *"><input data-testid="user-name" value={form.name} onChange={set("name")} className={inputCls} /></Field>
          <Field label="Email *"><input data-testid="user-email" type="email" value={form.email} onChange={set("email")} disabled={!!editing} className={inputCls} /></Field>
          <Field label={editing ? "Nouveau mot de passe (vide = inchangé)" : "Mot de passe *"}>
            <input data-testid="user-password" type="password" value={form.password} onChange={set("password")} className={inputCls} />
          </Field>
          <Field label="Rôle">
            <select data-testid="user-role" value={form.role} onChange={(e) => { setForm({ ...form, role: e.target.value, permissions: null }); setCustomPerms(false); }} className={selectCls}>
              {Object.entries(meta.roles).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
        </div>
        {editing && (
          <Field label="Compte actif">
            <input data-testid="user-active" type="checkbox" checked={editing.active}
              onChange={async (e) => {
                try { await api.patch(`/users/${editing.id}`, { active: e.target.checked }); toast.success("Statut mis à jour"); load(); }
                catch (err) { toast.error(errMsg(err)); }
              }} className="h-5 w-5 accent-primary" />
          </Field>
        )}
        <div className="mt-4">
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input data-testid="user-custom-perms" type="checkbox" checked={customPerms}
              onChange={(e) => { setCustomPerms(e.target.checked); if (e.target.checked && form.permissions == null) setForm({ ...form, permissions: [] }); }}
              className="h-4 w-4 accent-primary" />
            Permissions personnalisées (remplacent celles du rôle)
          </label>
          {customPerms && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5 mt-3 max-h-64 overflow-y-auto border rounded-lg p-3" data-testid="perms-grid">
              {meta.permissions.map((p) => (
                <label key={p} className="flex items-center gap-2 text-xs font-medium">
                  <input type="checkbox" data-testid={`perm-${p}`} checked={(form.permissions || []).includes(p)} onChange={() => togglePerm(p)} className="h-4 w-4 accent-primary" />
                  {PERM_LABELS[p] || p}
                </label>
              ))}
            </div>
          )}
        </div>
        <button data-testid="save-user" onClick={save} disabled={!form.name || (!editing && (!form.email || !form.password))} className={`${btnPrimary} w-full mt-4`}>
          {editing ? "Enregistrer" : "Créer l'utilisateur"}
        </button>
      </Modal>

      <Modal open={!!confirmDel} onClose={() => setConfirmDel(null)} title="Supprimer l'utilisateur">
        <p className="text-sm">Supprimer <b>{confirmDel?.name}</b> ({confirmDel?.email}) ?</p>
        <div className="flex gap-2 mt-4">
          <button data-testid="confirm-delete-user" onClick={del} className={`${btnDanger} flex-1`}>Supprimer</button>
          <button onClick={() => setConfirmDel(null)} className={btnSecondary}>Annuler</button>
        </div>
      </Modal>
    </div>
  );
}
